/**
 * Co-Pilot Panel Component
 * Right-side panel for agent-facing AI assistant with MCP integration
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  Collapse,
  Alert
} from '@mui/material';
import {
  Send,
  Clear,
  ContentCopy,
  ExpandMore,
  ExpandLess,
  Psychology,
  Build,
  Info
} from '@mui/icons-material';
import { MarkdownRenderer } from '../MarkdownRenderer';

interface CoPilotMessage {
  id: number;
  role: 'agent' | 'assistant';
  content: string;
  tool_calls?: Array<{ tool: string; arguments: any }>;
  tool_results?: Array<{ tool: string; result: any }>;
  created_at: string;
}

interface CoPilotPanelProps {
  conversationId: number;
  onClose?: () => void;
}

// Helper function to clean think tags from AI response
const cleanThinkTags = (text: string): string => {
  if (!text) return text;

  let cleaned = text;

  // Remove <think>...</think> blocks
  cleaned = cleaned.replace(/<think>[\s\S]*?<\/think>/gi, '');

  // Remove <thinking>...</thinking> blocks
  cleaned = cleaned.replace(/<thinking>[\s\S]*?<\/thinking>/gi, '');

  // Clean up extra whitespace and newlines
  cleaned = cleaned.replace(/\n\s*\n\s*\n+/g, '\n\n');
  cleaned = cleaned.trim();

  return cleaned;
};

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1';

const CoPilotPanel: React.FC<CoPilotPanelProps> = ({ conversationId, onClose }) => {
  const [messages, setMessages] = useState<CoPilotMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showContext, setShowContext] = useState(false);
  const [context, setContext] = useState<any>(null);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [expandedTools, setExpandedTools] = useState<{ [key: number]: boolean }>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  // Load message history
  useEffect(() => {
    loadHistory();
    loadContext();
  }, [conversationId]);

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE}/copilot/conversations/${conversationId}/history`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (err) {
      console.error('Failed to load Co-Pilot history:', err);
    }
  };

  const loadContext = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE}/copilot/conversations/${conversationId}/context`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setContext(data);
      }
    } catch (err) {
      console.error('Failed to load context:', err);
    }
  };

  const handleSend = async () => {
    if (!inputText.trim() || streaming) return;

    const userMessage = inputText.trim();
    setInputText('');
    setError(null);
    setStreaming(true);
    setStreamingMessage('');

    // Add user message immediately
    const tempUserMessage: CoPilotMessage = {
      id: Date.now(),
      role: 'agent',
      content: userMessage,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMessage]);

    try {
      const token = localStorage.getItem('token');

      // Use fetch with streaming (POST with auth headers)
      const response = await fetch(
        `${API_BASE}/copilot/conversations/${conversationId}/chat`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            message: userMessage,
            include_context: true,
            stream: true
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get Co-Pilot response');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let accumulatedMessage = '';
      let toolCalls: any[] = [];
      let toolResults: any[] = [];

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);

              if (data === '[DONE]') continue;

              try {
                const parsed = JSON.parse(data);

                if (parsed.type === 'delta') {
                  accumulatedMessage += parsed.content || '';
                  setStreamingMessage(accumulatedMessage);
                } else if (parsed.type === 'tool') {
                  toolCalls.push({
                    tool: parsed.tool_name,
                    arguments: parsed.arguments
                  });
                  if (parsed.result) {
                    toolResults.push({
                      tool: parsed.tool_name,
                      result: parsed.result
                    });
                  }
                } else if (parsed.type === 'done') {
                  // Finalize assistant message - clean think tags before saving
                  const cleanedMessage = cleanThinkTags(accumulatedMessage);
                  const assistantMessage: CoPilotMessage = {
                    id: Date.now() + 1,
                    role: 'assistant',
                    content: cleanedMessage,
                    tool_calls: toolCalls.length > 0 ? toolCalls : undefined,
                    tool_results: toolResults.length > 0 ? toolResults : undefined,
                    created_at: new Date().toISOString()
                  };
                  setMessages(prev => [...prev, assistantMessage]);
                  setStreamingMessage('');
                } else if (parsed.type === 'error') {
                  setError(parsed.error || 'An error occurred');
                }
              } catch (e) {
                console.error('Failed to parse SSE data:', e);
              }
            }
          }
        }
      }
    } catch (err: any) {
      console.error('Co-Pilot error:', err);
      setError(err.message || 'Failed to get response from Co-Pilot');
    } finally {
      setStreaming(false);
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Clear Co-Pilot conversation history?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE}/copilot/conversations/${conversationId}/clear`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        setMessages([]);
        setStreamingMessage('');
      }
    } catch (err) {
      console.error('Failed to clear history:', err);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const toggleToolExpansion = (messageId: number) => {
    setExpandedTools(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  return (
    <Paper
      elevation={3}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '2px solid #e0e0e0'
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Psychology color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Co-Pilot Assistant
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="View Context">
              <IconButton size="small" onClick={() => setShowContext(!showContext)}>
                <Info fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear History">
              <IconButton size="small" onClick={handleClearHistory}>
                <Clear fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Ask questions about this conversation or query backend systems
        </Typography>
      </Box>

      {/* Context Display */}
      <Collapse in={showContext}>
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Conversation Context
          </Typography>
          {context && (
            <Box sx={{ fontSize: '0.75rem' }}>
              <Typography variant="caption" display="block">
                <strong>Customer:</strong> {context.customer?.name} ({context.customer?.email})
              </Typography>
              <Typography variant="caption" display="block">
                <strong>Status:</strong> {context.conversation_status} | <strong>Priority:</strong> {context.conversation_priority}
              </Typography>
              <Typography variant="caption" display="block">
                <strong>Channel:</strong> {context.channel?.name} ({context.channel?.type})
              </Typography>
              {context.labels && context.labels.length > 0 && (
                <Typography variant="caption" display="block">
                  <strong>Labels:</strong> {context.labels.join(', ')}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Error Display */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {messages.length === 0 && !streaming && (
          <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
            <Psychology sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
            <Typography variant="body2">
              Start a conversation with Co-Pilot
            </Typography>
            <Typography variant="caption">
              Ask about this customer, search data, or get suggestions
            </Typography>
          </Box>
        )}

        {messages.map((message) => (
          <Box key={message.id} sx={{ mb: 2 }}>
            <Card
              elevation={1}
              sx={{
                bgcolor: message.role === 'agent' ? '#e3f2fd' : '#f5f5f5',
                borderLeft: message.role === 'agent' ? '3px solid #1976d2' : '3px solid #757575'
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Chip
                    label={message.role === 'agent' ? 'You' : 'Co-Pilot'}
                    size="small"
                    color={message.role === 'agent' ? 'primary' : 'default'}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  {message.role === 'assistant' && (
                    <Tooltip title="Copy">
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(message.content)}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {/* Render message content - use MarkdownRenderer for assistant messages */}
                {message.role === 'assistant' ? (
                  <MarkdownRenderer
                    content={cleanThinkTags(message.content)}
                    sx={{
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                      '& p': { margin: '0.3em 0' },
                      '& img': {
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      },
                      '& a': {
                        color: '#1976d2',
                        textDecoration: 'none',
                        wordBreak: 'break-all'
                      }
                    }}
                  />
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {message.content}
                  </Typography>
                )}

                {/* Tool Usage Indicators */}
                {message.tool_calls && message.tool_calls.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      startIcon={<Build />}
                      endIcon={expandedTools[message.id] ? <ExpandLess /> : <ExpandMore />}
                      onClick={() => toggleToolExpansion(message.id)}
                      sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      {message.tool_calls.length} tool{message.tool_calls.length > 1 ? 's' : ''} used
                    </Button>
                    <Collapse in={expandedTools[message.id]}>
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, fontSize: '0.7rem' }}>
                        {message.tool_calls.map((tool, idx) => (
                          <Box key={idx} sx={{ mb: 1 }}>
                            <Typography variant="caption" fontWeight="bold">
                              🔧 {tool.tool}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ ml: 2, fontFamily: 'monospace' }}>
                              {JSON.stringify(tool.arguments, null, 2)}
                            </Typography>
                            {message.tool_results && message.tool_results[idx] && (
                              <Typography variant="caption" display="block" sx={{ ml: 2, mt: 0.5, color: 'success.main' }}>
                                ✓ Result: {String(message.tool_results[idx].result).substring(0, 100)}...
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {new Date(message.created_at).toLocaleTimeString()}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}

        {/* Streaming Message */}
        {streaming && streamingMessage && (
          <Box sx={{ mb: 2 }}>
            <Card elevation={1} sx={{ bgcolor: '#f5f5f5', borderLeft: '3px solid #757575' }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Chip
                  label="Co-Pilot"
                  size="small"
                  color="default"
                  sx={{ height: 20, fontSize: '0.7rem', mb: 0.5 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <MarkdownRenderer
                    content={cleanThinkTags(streamingMessage)}
                    sx={{
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                      '& p': { margin: '0.3em 0' },
                      flex: 1
                    }}
                  />
                  <CircularProgress size={12} sx={{ ml: 1, mt: 0.5 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={3}
            placeholder="Ask Co-Pilot anything..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={streaming}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!inputText.trim() || streaming}
          >
            {streaming ? <CircularProgress size={24} /> : <Send />}
          </IconButton>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Press Enter to send, Shift+Enter for new line
        </Typography>
      </Box>
    </Paper>
  );
};

export default CoPilotPanel;
